import React, { useEffect, useState, useCallback } from 'react';
import { Save, Wallet, QrCode, Building2, FileSignature, Smartphone, AlertCircle } from 'lucide-react';
import NonGstPayments from './NonGstPayments';

const PATH = '/super-admin/company-settings';

const METHOD_OPTIONS = [
  { id: 'upi',      label: 'UPI / QR' },
  { id: 'razorpay', label: 'Razorpay (Cards / NetBanking)' },
  { id: 'cash',     label: 'Cash' },
  { id: 'cheque',   label: 'Cheque / DD' },
  { id: 'neft',     label: 'NEFT / Bank Transfer' },
];

export default function PaymentPanelTab({ api }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [err, setErr] = useState(null);
  const [cat, setCat] = useState('gst'); // 'gst' (existing settings) | 'non_gst' (new receipts)

  const load = useCallback(async () => {
    try {
      const r = await api('GET', PATH);
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load settings');
    }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggleMethod = (id) => setData((d) => {
    const cur = new Set(d.payment_methods || []);
    if (cur.has(id)) cur.delete(id); else cur.add(id);
    return { ...d, payment_methods: Array.from(cur) };
  });

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      const r = await api('PATCH', PATH, data);
      setData(r.data);
      setToast('Saved — new invoices will use these settings.');
      setTimeout(() => setToast(null), 2500);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  if (!data) return <div className="p-8 text-slate-500">Loading payment panel…</div>;

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-6xl" data-testid="payment-panel-tab">
      {/* Category tabs — GST (existing) vs Non-GST (new) */}
      <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="pp-categories">
        <button onClick={() => setCat('gst')} className={`px-4 py-1.5 rounded-md text-sm font-semibold ${cat === 'gst' ? 'bg-emerald-600 text-white' : 'text-slate-600'}`} data-testid="pp-cat-gst">GST Payments</button>
        <button onClick={() => setCat('non_gst')} className={`px-4 py-1.5 rounded-md text-sm font-semibold ${cat === 'non_gst' ? 'bg-emerald-600 text-white' : 'text-slate-600'}`} data-testid="pp-cat-non-gst">Non-GST Payments</button>
      </div>

      {cat === 'non_gst' && <NonGstPayments api={api} />}

      {cat === 'gst' && (<>
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Wallet size={22} className="text-emerald-600" /> Payment & Invoice Settings
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Controls the UPI/QR, bank, GSTIN, signatures and stamp that appear on every invoice PDF — updates apply immediately.
          </p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="payment-panel-save"
          className="inline-flex items-center gap-2 px-5 h-11 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-md disabled:opacity-60"
        >
          <Save size={16} /> {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>

      {toast && <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-2 rounded-md text-sm">{toast}</div>}
      {err && <div className="bg-rose-50 border border-rose-200 text-rose-800 px-4 py-2 rounded-md text-sm flex gap-2 items-center"><AlertCircle size={14}/> {err}</div>}

      {/* COMPANY */}
      <Section title="Company Identity" icon={<Building2 size={16}/>}>
        <Grid>
          <F label="Company Name"><I value={data.company_name} onChange={(v) => set('company_name', v)} testid="pp-company" /></F>
          <F label="Brand Short (logo text)"><I value={data.brand_short} onChange={(v) => set('brand_short', v)} maxLength={6} testid="pp-brand-short" /></F>
          <F label="GSTIN"><I value={data.gstin} onChange={(v) => set('gstin', v.toUpperCase())} testid="pp-gstin" /></F>
          <F label="HSN Code"><I value={data.hsn_code} onChange={(v) => set('hsn_code', v)} testid="pp-hsn" /></F>
          <F label="Email"><I value={data.email} onChange={(v) => set('email', v)} testid="pp-email" /></F>
          <F label="Phone (Primary)"><I value={data.phone_primary} onChange={(v) => set('phone_primary', v)} testid="pp-phone" /></F>
          <F label="Phone (Secondary)"><I value={data.phone_secondary || ''} onChange={(v) => set('phone_secondary', v)} testid="pp-phone2" /></F>
          <F label="Address Line 1"><I value={data.address_line1} onChange={(v) => set('address_line1', v)} testid="pp-addr1" /></F>
          <F label="Address Line 2"><I value={data.address_line2 || ''} onChange={(v) => set('address_line2', v)} testid="pp-addr2" /></F>
          <F label="Address Line 3"><I value={data.address_line3 || ''} onChange={(v) => set('address_line3', v)} testid="pp-addr3" /></F>
          <F label="City"><I value={data.city} onChange={(v) => set('city', v)} testid="pp-city" /></F>
          <F label="State"><I value={data.state} onChange={(v) => set('state', v)} testid="pp-state" /></F>
          <F label="PIN"><I value={data.pin_code} onChange={(v) => set('pin_code', v)} testid="pp-pin" /></F>
        </Grid>
      </Section>

      {/* UPI / QR */}
      <Section title="UPI & QR Code (auto-filled amount on Scan)" icon={<QrCode size={16}/>}>
        <Grid>
          <F label="UPI ID *" hint="Customers scanning the invoice QR will pay to this VPA"><I value={data.upi_id} onChange={(v) => set('upi_id', v)} testid="pp-upi" /></F>
          <F label="UPI Display Name"><I value={data.upi_display_name} onChange={(v) => set('upi_display_name', v)} testid="pp-upi-name" /></F>
          <F label="GPay Number"><I value={data.gpay_number || ''} onChange={(v) => set('gpay_number', v)} testid="pp-gpay" /></F>
          <F label="Custom QR Image URL (optional)" hint="If set, replaces the auto-generated UPI QR with your branded SBI QR image"><I value={data.qr_image_url || ''} onChange={(v) => set('qr_image_url', v)} testid="pp-qr-url" /></F>
        </Grid>
      </Section>

      {/* BANK */}
      <Section title="Bank Details (NEFT / IMPS / RTGS)" icon={<Building2 size={16}/>}>
        <Grid>
          <F label="Bank Name"><I value={data.bank_name} onChange={(v) => set('bank_name', v)} testid="pp-bank" /></F>
          <F label="Account Name"><I value={data.bank_account_name} onChange={(v) => set('bank_account_name', v)} testid="pp-acc-name" /></F>
          <F label="Account Number"><I value={data.bank_account_no} onChange={(v) => set('bank_account_no', v)} testid="pp-acc-no" /></F>
          <F label="IFSC"><I value={data.bank_ifsc} onChange={(v) => set('bank_ifsc', v.toUpperCase())} testid="pp-ifsc" /></F>
          <F label="Branch"><I value={data.bank_branch} onChange={(v) => set('bank_branch', v)} testid="pp-branch" /></F>
        </Grid>
      </Section>

      {/* SIGNATURES */}
      <Section title="Signatures & Stamp" icon={<FileSignature size={16}/>}>
        <Grid>
          <F label="Signatory Name"><I value={data.signatory_name} onChange={(v) => set('signatory_name', v)} testid="pp-sig-name" /></F>
          <F label="Signatory Designation"><I value={data.signatory_designation} onChange={(v) => set('signatory_designation', v)} testid="pp-sig-desg" /></F>
          <F label="Signature Image URL (PNG transparent)"><I value={data.signature_url || ''} onChange={(v) => set('signature_url', v)} testid="pp-sig-url" /></F>
          <F label="Company Stamp Image URL (PNG transparent)"><I value={data.stamp_url || ''} onChange={(v) => set('stamp_url', v)} testid="pp-stamp-url" /></F>
        </Grid>
      </Section>

      {/* GST */}
      <Section title="GST Rates" icon={<Smartphone size={16}/>}>
        <Grid>
          <F label="CGST %"><I type="number" value={data.cgst_pct} onChange={(v) => set('cgst_pct', Number(v))} testid="pp-cgst" /></F>
          <F label="SGST %"><I type="number" value={data.sgst_pct} onChange={(v) => set('sgst_pct', Number(v))} testid="pp-sgst" /></F>
        </Grid>
      </Section>

      {/* METHODS */}
      <Section title="Accepted Payment Methods" icon={<Wallet size={16}/>}>
        <div className="flex flex-wrap gap-2" data-testid="pp-methods">
          {METHOD_OPTIONS.map(m => {
            const on = (data.payment_methods || []).includes(m.id);
            return (
              <button key={m.id} type="button" onClick={() => toggleMethod(m.id)}
                data-testid={`pp-method-${m.id}`}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition ${on ? 'bg-emerald-600 text-white shadow' : 'bg-white border border-slate-300 text-slate-700 hover:border-emerald-400'}`}
              >{m.label}</button>
            );
          })}
        </div>
      </Section>

      <div className="text-xs text-slate-400">Last updated: {data.updated_at ? new Date(data.updated_at).toLocaleString('en-IN') : 'never'}</div>
      </>)}
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
      <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">{icon}{title}</h3>
      {children}
    </section>
  );
}
function Grid({ children }) { return <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{children}</div>; }
function F({ label, hint, children }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold text-slate-700 mb-1">{label}</div>
      {hint && <div className="text-[10px] text-slate-500 mb-1">{hint}</div>}
      {children}
    </label>
  );
}
function I({ value, onChange, type = 'text', maxLength, testid }) {
  return (
    <input
      type={type}
      value={value ?? ''}
      maxLength={maxLength}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="h-10 w-full px-3 rounded-lg border border-slate-300 focus:border-emerald-500 outline-none text-sm"
    />
  );
}
